import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, LogOut, RefreshCw, Phone, Mail, MapPin, Clock, Download,
  ShieldCheck, Inbox, CheckCircle2, CircleDot,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import SeoHead from "@/components/site/SeoHead";
import { verifyAdmin, fetchLeads, fetchStats, updateLeadStatus } from "@/lib/api";
import { COMPANY } from "@/data/content";

const TOKEN_KEY = "schoessow_admin_token";

export default function AdminLeads() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(!!token);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (tk) => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(tk), fetchStats(tk)]);
      setLeads(l);
      setStats(s);
    } catch (e) {
      toast.error("Daten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const check = async () => {
      if (!token) { setChecking(false); return; }
      try {
        await verifyAdmin(token);
        setAuthed(true);
        await load(token);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } finally {
        setChecking(false);
      }
    };
    check();
  }, [token, load]);

  const onLogin = async (e) => {
    e.preventDefault();
    try {
      await verifyAdmin(input);
      localStorage.setItem(TOKEN_KEY, input);
      setToken(input);
      setAuthed(true);
      await load(input);
      toast.success("Erfolgreich angemeldet.");
    } catch (err) {
      toast.error("Ungültiges Passwort.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setLeads([]);
  };

  const toggleStatus = async (lead) => {
    const next = lead.status === "new" ? "contacted" : "new";
    try {
      await updateLeadStatus(token, lead.id, next);
      await load(token);
    } catch (e) {
      toast.error("Status konnte nicht geändert werden.");
    }
  };

  const exportCsv = () => {
    const headers = ["Datum", "Name", "Telefon", "E-Mail", "Straße", "PLZ/Ort", "Rückrufzeit", "Nachricht", "Status", "E-Mail-Versand"];
    const rows = leads.map((l) => [
      new Date(l.created_at).toLocaleString("de-DE"), l.name, l.phone, l.email,
      l.street, l.city, l.callback_time, (l.message || "").replace(/\n/g, " "), l.status, l.email_status,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const emailBadge = (s) => {
    const map = {
      sent: { label: "gesendet", cls: "bg-green-100 text-green-800" },
      pending: { label: "in Bearbeitung", cls: "bg-amber-100 text-amber-800" },
      error: { label: "Fehler", cls: "bg-red-100 text-red-800" },
      disabled: { label: "deaktiviert", cls: "bg-slate-100 text-slate-700" },
    };
    const m = map[s] || map.disabled;
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded ${m.cls}`}>E-Mail: {m.label}</span>;
  };

  if (checking) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[color:var(--brand-navy)]" /></div>;
  }

  if (!authed) {
    return (
      <>
        <SeoHead title="Admin – Anmeldung" description="Interner Bereich" noindex />
        <div className="min-h-screen grid place-items-center bg-secondary px-4">
          <form onSubmit={onLogin} className="w-full max-w-sm rounded-2xl border border-border bg-white p-8 card-shadow" data-testid="admin-login-form">
            <div className="flex items-center gap-2 text-[color:var(--brand-navy)]">
              <ShieldCheck className="h-6 w-6 text-[color:var(--brand-green-alt)]" />
              <h1 className="font-display text-3xl">ADMIN LOGIN</h1>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">Lead-Postfach – {COMPANY.name}</p>
            <div className="mt-6">
              <Label htmlFor="pw" className="font-semibold">Passwort</Label>
              <Input id="pw" type="password" value={input} onChange={(e) => setInput(e.target.value)} className="mt-1.5 h-12" data-testid="admin-token-input" placeholder="Zugangs-Passwort" />
            </div>
            <Button type="submit" className="mt-5 w-full h-12 rounded-xl bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-alt)] text-white font-bold" data-testid="admin-login-button">Anmelden</Button>
          </form>
        </div>
      </>
    );
  }

  return (
    <>
      <SeoHead title="Admin – Lead-Postfach" description="Interner Bereich" noindex />
      <div className="min-h-screen bg-secondary">
        <header className="bg-[color:var(--brand-navy)] text-white">
          <div className="container-x py-5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Inbox className="h-6 w-6 text-[color:var(--brand-green)]" />
              <h1 className="font-display text-2xl sm:text-3xl">LEAD-POSTFACH</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => load(token)} variant="outline" size="sm" className="h-10 rounded-lg bg-white/10 border-white/25 text-white hover:bg-white/20" data-testid="admin-refresh-button">
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
              <Button onClick={exportCsv} variant="outline" size="sm" className="h-10 rounded-lg bg-white/10 border-white/25 text-white hover:bg-white/20" data-testid="admin-export-button">
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
              <Button onClick={logout} variant="outline" size="sm" className="h-10 rounded-lg bg-white/10 border-white/25 text-white hover:bg-white/20" data-testid="admin-logout-button">
                <LogOut className="h-4 w-4 mr-1" /> Abmelden
              </Button>
            </div>
          </div>
        </header>

        <div className="container-x py-8">
          <div className="grid grid-cols-3 gap-4 max-w-xl">
            <StatCard label="Gesamt" value={stats.total} />
            <StatCard label="Neu" value={stats.new} accent />
            <StatCard label="Kontaktiert" value={stats.contacted} />
          </div>

          <div className="mt-8">
            {loading && leads.length === 0 ? (
              <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[color:var(--brand-navy)]" /></div>
            ) : leads.length === 0 ? (
              <div className="rounded-2xl border border-border bg-white p-12 text-center card-shadow" data-testid="admin-empty">
                <Inbox className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="mt-3 text-lg font-semibold text-[color:var(--brand-navy)]">Noch keine Anfragen</p>
                <p className="text-sm text-muted-foreground">Neue Rückruf-Anfragen erscheinen hier automatisch.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="admin-leads-list">
                {leads.map((l) => (
                  <div key={l.id} className="rounded-2xl border border-border bg-white p-5 card-shadow" data-testid="admin-lead-card">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-lg font-bold text-[color:var(--brand-navy)]">{l.name}</h3>
                          {l.status === "new"
                            ? <Badge className="bg-[color:var(--brand-green)] text-white">Neu</Badge>
                            : <Badge variant="outline" className="text-muted-foreground">Kontaktiert</Badge>}
                          {emailBadge(l.email_status)}
                        </div>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[15px] text-[color:var(--brand-navy)]">
                          <a href={`tel:${l.phone}`} className="flex items-center gap-2 font-semibold hover:underline"><Phone className="h-4 w-4 text-[color:var(--brand-green-alt)]" /> {l.phone}</a>
                          {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:underline"><Mail className="h-4 w-4 text-[color:var(--brand-green-alt)]" /> {l.email}</a>}
                          {(l.street || l.city) && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[color:var(--brand-green-alt)]" /> {[l.street, l.city].filter(Boolean).join(", ")}</span>}
                          {l.callback_time && <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-[color:var(--brand-green-alt)]" /> {l.callback_time}</span>}
                        </div>
                        {l.message && <p className="mt-3 text-sm text-muted-foreground bg-secondary rounded-lg p-3 whitespace-pre-wrap">{l.message}</p>}
                        <p className="mt-2 text-xs text-muted-foreground">Eingegangen: {new Date(l.created_at).toLocaleString("de-DE")}</p>
                      </div>
                      <Button onClick={() => toggleStatus(l)} variant="outline" className="rounded-xl shrink-0" data-testid="admin-toggle-status">
                        {l.status === "new" ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Als kontaktiert</> : <><CircleDot className="h-4 w-4 mr-1" /> Als neu</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-5 card-shadow ${accent ? "bg-[color:var(--brand-green)]/10 border-[color:var(--brand-green)]/40" : "bg-white border-border"}`}>
      <p className="text-sm font-semibold text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-4xl text-[color:var(--brand-navy)]">{value}</p>
    </div>
  );
}
